import React, { useState } from 'react';
import BlueCallToAction from '../components/common/BlueCallToAction';

const DocumentationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  
  // Documentation sections
  const sections = [
    { id: 'getting-started', name: 'Getting Started' },
    { id: 'core-concepts', name: 'Core Concepts' },
    { id: 'api-reference', name: 'API Reference' },
    { id: 'integrations', name: 'Integrations' },
    { id: 'troubleshooting', name: 'Troubleshooting' },
    { id: 'faq', name: 'FAQ' }
  ];
  
  // Sample documentation content
  const docContent = {
    'getting-started': {
      title: 'Getting Started with Videxa',
      content: [
        {
          id: 'introduction',
          title: 'Introduction',
          text: 'Videxa is a powerful AI healthcare platform designed to streamline your organization\'s healthcare operations. This guide will help you get started with setting up and using the platform effectively.'
        },
        {
          id: 'installation',
          title: 'Installation',
          text: 'To install Videxa, follow these steps:\n1. Sign up for an account\n2. Download the Videxa client\n3. Install the client on your system\n4. Configure your settings\n5. Connect to your organization\'s workspace'
        },
        {
          id: 'first-automation',
          title: 'Creating Your First Automation',
          text: 'Learn how to create your first automation workflow:\n1. Navigate to the Workflows section\n2. Click "New Workflow"\n3. Select a trigger event\n4. Configure the automation steps\n5. Test and deploy your workflow'
        }
      ]
    },
    'core-concepts': {
      title: 'Core Concepts',
      content: [
        {
          id: 'agents',
          title: 'Agents',
          text: 'Agents are the core building blocks of the Videxa platform. Each agent is designed to perform specific healthcare tasks and can be combined to create complex automation workflows.'
        },
        {
          id: 'workflows',
          title: 'Workflows',
          text: 'Workflows define how agents interact and the sequence of operations. A workflow consists of triggers, conditions, and actions.'
        },
        {
          id: 'integrations',
          title: 'Integrations',
          text: 'Integrations allow Videxa to connect with external healthcare systems and services, enabling seamless data flow and automation across your entire technology stack.'
        }
      ]
    },
    'api-reference': {
      title: 'API Reference',
      content: [
        {
          id: 'authentication',
          title: 'Authentication',
          text: 'All API requests require authentication using API keys. You can generate API keys in your account settings.'
        },
        {
          id: 'endpoints',
          title: 'Endpoints',
          text: 'The Videxa API provides the following main endpoints:\n- /api/v1/agents\n- /api/v1/workflows\n- /api/v1/integrations\n- /api/v1/users\n- /api/v1/organizations'
        },
        {
          id: 'rate-limits',
          title: 'Rate Limits',
          text: 'API requests are subject to rate limiting to ensure fair usage. The default rate limit is 100 requests per minute per API key.'
        }
      ]
    },
    'integrations': {
      title: 'Integrations Guide',
      content: [
        {
          id: 'available-integrations',
          title: 'Available Integrations',
          text: 'Videxa supports integrations with various healthcare systems including EHR platforms, revenue cycle management tools, data analytics services, and custom APIs.'
        },
        {
          id: 'setting-up-integrations',
          title: 'Setting Up Integrations',
          text: 'To set up an integration:\n1. Go to the Integrations section\n2. Select the service you want to integrate with\n3. Follow the authentication process\n4. Configure the integration settings\n5. Test the connection'
        },
        {
          id: 'custom-integrations',
          title: 'Custom Integrations',
          text: 'For systems without pre-built integrations, you can create custom integrations using our API and webhooks.'
        }
      ]
    },
    'troubleshooting': {
      title: 'Troubleshooting',
      content: [
        {
          id: 'common-issues',
          title: 'Common Issues',
          text: 'This section covers common issues users encounter and their solutions.'
        },
        {
          id: 'error-codes',
          title: 'Error Codes',
          text: 'A comprehensive list of error codes and their meanings to help diagnose problems.'
        },
        {
          id: 'support',
          title: 'Getting Support',
          text: 'If you need additional help, contact our support team at support@videxa.com or use the in-app chat support.'
        }
      ]
    },
    'faq': {
      title: 'Frequently Asked Questions',
      content: [
        {
          id: 'general-faq',
          title: 'General Questions',
          text: 'Answers to common questions about Videxa, its capabilities, and use cases.'
        },
        {
          id: 'technical-faq',
          title: 'Technical Questions',
          text: 'Answers to technical questions about platform requirements, performance, and security.'
        },
        {
          id: 'support-faq',
          title: 'Support Questions',
          text: 'Information about getting help, support channels, and response times.'
        }
      ]
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    // In a real application, this would search through documentation content
    alert(`Searching for: ${searchQuery}`);
  };

  return (
    <div className="documentation-page">
      {/* Hero Section */}
      <section 
        className="py-20"
        style={{
          background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-6xl font-bold mb-6" 
              style={{ color: '#111827' }}
            >
              Platform <span style={{ color: '#FFC000' }}>Documentation</span>
            </h1>
            <p 
              className="text-xl md:text-2xl mb-8" 
              style={{ color: '#374151' }}
            >
              Everything you need to know about using Videxa to transform your healthcare operations.
            </p>
            <form className="max-w-md mx-auto" onSubmit={handleSearch}>
              <div className="flex">
                <input
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-l-lg border-0 text-gray-900 focus:ring-2"
                  style={{ focusRingColor: '#FFC000' }}
                />
                <button 
                  type="submit"
                  style={{ 
                    backgroundColor: '#0E3056',
                    color: 'white'
                  }}
                  className="px-6 py-3 hover:bg-opacity-90 rounded-r-lg transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Main Documentation Content */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-12 max-w-6xl mx-auto">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <nav className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 sticky top-6">
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          activeSection === section.id 
                            ? 'bg-primary-600 text-white' 
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                        onClick={() => setActiveSection(section.id)}
                      >
                        {section.name}
                      </button>
                      {activeSection === section.id && docContent[section.id] && (
                        <ul className="mt-2 ml-4 space-y-1">
                          {docContent[section.id].content.map((item) => (
                            <li key={item.id}>
                              <a 
                                href={`#${item.id}`} 
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                              >
                                {item.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 bg-primary-50 dark:bg-primary-900 rounded-lg p-4">
                  <h3 className="font-semibold mb-2 text-primary-900 dark:text-primary-100">Need Help?</h3>
                  <p className="text-sm text-primary-700 dark:text-primary-300 mb-3">
                    Our support team is ready to assist you with any questions.
                  </p>
                  <a 
                    href="/contact" 
                    className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    Contact Support
                  </a>
                </div>
              </nav>
            </div>

            {/* Documentation Main Content */}
            <div className="lg:col-span-3">
              {docContent[activeSection] && (
                <>
                  <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
                    {docContent[activeSection].title}
                  </h2>
                  <div className="space-y-8">
                    {docContent[activeSection].content.map((item) => (
                      <div 
                        key={item.id} 
                        id={item.id}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                      >
                        <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                          {item.title}
                        </h3>
                        <div className="prose dark:prose-invert max-w-none">
                          {item.text.split('\n').map((line, index) => (
                            <p key={index} className="text-gray-600 dark:text-gray-300 mb-2">
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                    <a 
                      href="#" 
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      ← Previous Section
                    </a>
                    <a 
                      href="#" 
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Next Section →
                    </a>
                  </div>

                  <div className="mt-8 bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                    <h4 className="font-semibold mb-4 text-gray-900 dark:text-white">
                      Was this documentation helpful?
                    </h4>
                    <div className="space-x-4">
                      <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">
                        Yes
                      </button>
                      <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors">
                        No
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to implement these features?"
        description="Our team can help you get started with Videxa and optimize your healthcare operations."
        primaryButtonText="Request Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Contact Support"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default DocumentationPage; 